// src/components/site-editor/SiteEditorPanel.jsx
import React, { useState, useCallback, useEffect } from 'react';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Save, Upload, Check } from 'lucide-react';
import SectionSelector from './SectionSelector';
import FieldRenderer from './FieldRenderer';
import ArrayEditor from './ArrayEditor';
import { useSiteEditor } from '../../hooks/useSiteEditor';
import { getEditableFieldsForSection } from './editableFields';
import { cn } from '../../utils/cn';

/**
 * Site Editor Panel - Right panel for editing content
 *
 * Features:
 * - Section selector dropdown
 * - Dynamic field rendering based on JSON structure
 * - Auto-save with debouncing
 * - Publish functionality
 */
const SiteEditorPanel = () => {
  const {
    currentSite,
    editing,
    isDirty,
    savingDraft,
    startEditing,
    updateEditingDraft,
    publishSite,
    hasUnpublishedChanges
  } = useSiteEditor();

  const [selectedSection, setSelectedSection] = useState('hero');
  const [publishStatus, setPublishStatus] = useState('idle'); // 'idle' | 'publishing' | 'published'

  // Start editing session when component mounts
  useEffect(() => {
    if (currentSite?.id && !editing.siteId) {
      console.log('🎬 SiteEditorPanel: Starting editing session');
      startEditing(currentSite.id);
    }
  }, [currentSite?.id, editing.siteId, startEditing]);

  // Handle field change - updates the draft in the store (triggers auto-save)
  const handleFieldChange = useCallback((path, value) => {
    if (!editing.draft) return;

    console.log('✏️ SiteEditorPanel: Field changed', { path, value });

    // Build updated draft with the change
    const newDraft = { ...editing.draft };
    const keys = path.split('.');
    let current = newDraft;

    // Navigate to the parent object
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!current[key]) {
        current[key] = {};
      }
      current = current[key];
    }

    // Set the value
    current[keys[keys.length - 1]] = value;

    // Update the draft in the store (this triggers auto-save)
    updateEditingDraft(newDraft);
  }, [editing.draft, updateEditingDraft]);

  // Handle publish
  const handlePublish = async () => {
    setPublishStatus('publishing');
    try {
      await publishSite(currentSite.id);
      setPublishStatus('published');
      setTimeout(() => setPublishStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to publish:', error);
      setPublishStatus('idle');
    }
  };

  // Get current section data from editing.draft
  const currentSectionData = editing.draft?.[selectedSection] || {};
  const templateType = currentSite?.template_type;
  const sectionConfig = getEditableFieldsForSection(selectedSection, templateType);
  const isTopLevelArray = sectionConfig.isTopLevelArray && Array.isArray(currentSectionData);

  // Determine save status for UI
  const saveStatus = savingDraft ? 'saving' : (isDirty ? 'unsaved' : 'saved');

  return (
    <div className="flex flex-col h-full bg-sidebar border-l border-sidebar-border">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-sidebar-border bg-sidebar p-4">
        <div className="space-y-4">
          {/* Section Selector */}
          <SectionSelector
            sections={Object.keys(editing.draft || {})}
            selectedSection={selectedSection}
            onSelectSection={setSelectedSection}
          />

          {/* Save Status */}
          <div className="flex items-center justify-between text-xs">
            <span className={cn(
              "transition-colors",
              saveStatus === 'saved' && "text-emerald-500",
              saveStatus === 'saving' && "text-blue-500",
              saveStatus === 'unsaved' && "text-orange-500"
            )}>
              {saveStatus === 'saved' && (
                <span className="flex items-center gap-1">
                  <Check className="w-3 h-3" /> All changes saved
                </span>
              )}
              {saveStatus === 'saving' && 'Saving...'}
              {saveStatus === 'unsaved' && 'Unsaved changes'}
            </span>

            {hasUnpublishedChanges && (
              <span className="text-orange-500 font-medium">
                Unpublished changes
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content - Scrollable Fields */}
      <ScrollArea className="flex-1">
        <div className="p-6 space-y-6">
          {isTopLevelArray ? (
            <ArrayEditor
              label={selectedSection.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()}
              items={currentSectionData}
              path={selectedSection}
              onChange={handleFieldChange}
              editableFields={sectionConfig.topLevelArrayFields || []}
              readOnly={true}
            />
          ) : Object.keys(currentSectionData).length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              No fields in this section
            </div>
          ) : (
            Object.entries(currentSectionData).map(([key, value]) => (
              <FieldRenderer
                key={key}
                fieldKey={key}
                value={value}
                path={`${selectedSection}.${key}`}
                onChange={handleFieldChange}
                section={selectedSection}
                templateType={templateType}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Footer - Publish Button */}
      <div className="flex-shrink-0 border-t border-sidebar-border bg-sidebar p-4">
        <Button
          onClick={handlePublish}
          disabled={!hasUnpublishedChanges || publishStatus === 'publishing'}
          className="w-full"
          size="lg"
        >
          {publishStatus === 'publishing' ? (
            <>
              <Save className="w-4 h-4 mr-2 animate-spin" />
              Publishing...
            </>
          ) : publishStatus === 'published' ? (
            <>
              <Check className="w-4 h-4 mr-2" />
              Published!
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Publish to Production
            </>
          )}
        </Button>
        {currentSite.site_url && (
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Will deploy to {currentSite.site_url}
          </p>
        )}
      </div>
    </div>
  );
};

export default SiteEditorPanel;
